/* eslint-disable no-restricted-syntax -- Local building blocks for the stores screens on the admin's own design system (shared/rd); see apps/admin/AGENTS.md. */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Copy, type LucideIcon } from "lucide-react";
import { Badge, Button, useToast, type ButtonSize, type ButtonVariant } from "@/shared/rd";

/**
 * Small pieces the stores screens need that the `@/shared/rd` kit does not
 * have (a card with the mock's header row, a copy-link button, a small
 * action menu). Built here rather than in the kit,
 * on its tokens only.
 */

/** The mock's card: a header row (badge, 15/600 title, one-line note, aside) and a body. */
export function SectionCard({
  badge,
  title,
  titleId,
  note,
  aside,
  children,
  bodyStyle,
  ...rest
}: {
  badge?: string;
  title: ReactNode;
  titleId?: string;
  note?: ReactNode;
  aside?: ReactNode;
  children?: ReactNode;
  bodyStyle?: CSSProperties;
} & Record<`data-${string}`, string | undefined>) {
  return (
    <section className="rd-card" style={{ overflow: "hidden" }} aria-labelledby={titleId} {...rest}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          padding: "14px 16px",
          borderBottom: children != null ? "1px solid var(--rd-line-row)" : undefined,
        }}
      >
        {badge ? <Badge id={badge} size={32} /> : null}
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3, flex: "1 1 200px", minWidth: 0 }}>
          <h2 id={titleId} style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
            {title}
          </h2>
          {note ? <span style={{ fontSize: 12, color: "var(--rd-text-tertiary)" }}>{note}</span> : null}
        </div>
        {aside ?? null}
      </div>
      {children != null ? (
        <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12, ...bodyStyle }}>
          {children}
        </div>
      ) : null}
    </section>
  );
}

/** A section title above a table (15/600) with actions at its end. */
export function SectionHead({ title, note, actions }: { title: ReactNode; note?: ReactNode; actions?: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{title}</h2>
      {note ? <span style={{ fontSize: 13, color: "var(--rd-text-tertiary)" }}>{note}</span> : null}
      <div className="rd-spacer" style={{ flex: 1 }} />
      {actions ?? null}
    </div>
  );
}

/** A row of buttons. */
export function Actions({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", ...style }}>{children}</div>;
}

/** Muted 13px text. */
export function Meta({ children, style, ...rest }: { children: ReactNode; style?: CSSProperties } & Record<`data-${string}`, string | undefined>) {
  return (
    <p style={{ margin: 0, fontSize: 13, color: "var(--rd-text-secondary)", lineHeight: 1.5, ...style }} {...rest}>
      {children}
    </p>
  );
}

/** Uppercase meta label ("WALLET TOP-UP REQUESTS"). */
export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--rd-text-meta)" }}>
      {children}
    </span>
  );
}

async function writeClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.append(area);
    area.select();
    const ok = document.execCommand("copy");
    area.remove();
    return ok;
  } catch {
    return false;
  }
}

/**
 * Copies a portal link and answers with a short neutral toast naming the link
 * ("Storefront link copied") — a quick result that changes no data.
 */
export function CopyLinkButton({
  path,
  label,
  copiedMessage,
  ariaLabel,
  variant = "ghost",
  size = "sm",
  icon = Copy,
}: {
  path: string;
  label: string;
  /** The toast after a copy, naming the link. */
  copiedMessage: string;
  /** Says WHICH link this copies. */
  ariaLabel: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: LucideIcon;
}) {
  const toast = useToast();
  return (
    <Button
      variant={variant}
      size={size}
      icon={icon}
      aria-label={ariaLabel}
      onClick={async () => {
        const link = path.startsWith("http") ? path : `${window.location.origin}${path}`;
        if (await writeClipboard(link)) {
          toast.show({ tone: "neutral", icon: Copy, message: copiedMessage });
        }
      }}
    >
      {label}
    </Button>
  );
}

export type ActionMenuItem = { key: string; label: string; disabled?: boolean; onSelect: () => void };

/**
 * A "more actions" button with a small menu under it: closes on select,
 * Escape and a click outside; arrow keys move between items.
 */
export function ActionMenu({ label, items, icon }: { label: string; items: ActionMenuItem[]; icon: LucideIcon }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement | null>(null);
  const list = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    list.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus();
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const move = (event: { key: string; preventDefault: () => void }) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const buttons = [...(list.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") ?? [])];
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === "ArrowDown" ? index + 1 : index - 1;
    buttons[(next + buttons.length) % buttons.length]?.focus();
  };
  return (
    <div ref={wrap} style={{ position: "relative", display: "inline-flex" }}>
      <Button
        variant="ghost"
        iconOnly
        icon={icon}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      />
      {open ? (
        <div
          ref={list}
          role="menu"
          aria-label={label}
          onKeyDown={move}
          style={{
            position: "absolute",
            insetInlineEnd: 0,
            top: "calc(100% + 4px)",
            zIndex: 30,
            minWidth: 220,
            padding: 4,
            borderRadius: "var(--rd-radius-lg)",
            background: "var(--rd-surface)",
            boxShadow: "var(--rd-shadow-modal)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              className="rd-listsec-row"
              style={{
                border: 0,
                background: "transparent",
                textAlign: "start",
                font: "inherit",
                fontSize: 14,
                color: item.disabled ? "var(--rd-text-tertiary)" : "var(--rd-text)",
                padding: "7px 10px",
                borderRadius: "var(--rd-radius-md)",
                cursor: item.disabled ? "default" : "pointer",
              }}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** The RD checkbox look with a form `name` (the kit's Checkbox takes none). */
export function NamedCheckbox({
  name,
  checked,
  onChange,
  disabled,
  children,
}: {
  name: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="rd-check rd-check-plain">
      <input
        type="checkbox"
        className="rd-check-input"
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="rd-check-text">
        <span className="rd-check-label">{children}</span>
      </span>
    </label>
  );
}
